import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Utils from './../../../../../../utils';
import style from './style/normal.scss';  // eslint-disable-line

/**
 * Horizontal score bar with even sections
 *
 * @example
 * <Normal
 *     scoreMin={1}
 *     scoreMax={1}
 *     scoreValue={1}
 *     fill={false}
 *     floor={true}
 *     classes={{ root: '', svgContainer: ''}}
 *     autoScaleWidth={true}
 *     scoreBarConfig={{
 *         barWidth: 36,
 *         barMargin: 0,
 *         barHeight: 8,
 *         activeBarHeight: 11,
 *         barBorderRadius: 5
 *     }}
 * />
 */
export default class Normal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            scoreValue: 0
        };
    }

    componentDidUpdate() {
        if (this.props.scoreValue !== this.state.scoreValue) {
            this.setState({
                scoreValue: this.props.scoreValue
            });
        }
    }

    isInteger(num) {
        return num % 1 === 0;
    }

    render() {
        const {
            scoreMin,
            scoreMax,
            fill,
            classes,
            autoScaleWidth,
            scoreBarConfig,
            scoreValue,
            children,
            floor
        } = this.props;

        // scoreValue that will be changed while building up the score bar (removed the bad practice of changing the prop directly)
        let calcScoreValue = scoreValue;

        const defaultConfig = {
            barWidth: 36,
            barMargin: 0,
            barHeight: 8,
            activeBarHeight: 11,
            barBorderRadius: 5
        };

        const combinedConfig = { ...defaultConfig, ...scoreBarConfig };

        // extract config
        const barWidth = combinedConfig.barWidth;
        const barMargin = combinedConfig.barMargin;
        const barHeight = combinedConfig.barHeight;
        const activeBarHeight = combinedConfig.activeBarHeight;
        const barBorderRadius = combinedConfig.barBorderRadius;

        // calculate sizes based on props
        const svgXOffset = barWidth + barMargin;
        const svgWidth = barWidth;
        const svgHeight = barHeight;
        const svgActiveHeight = activeBarHeight;
        const svgActiveYOffset = 0;
        const svgYOffset = (svgActiveHeight - svgHeight) / 2;
        const borderRadius = barBorderRadius;

        // calculate viewbox based on sizes of svg bars
        const viewBoxWidth = scoreMax * svgXOffset,
            viewBoxHeight = svgActiveHeight + svgActiveYOffset,
            elements = [],
            scores = [],
            elementsCompetencies = [];

        calcScoreValue = Utils.parseScore(calcScoreValue, scoreMin, scoreMax, floor);

        // in case scores should explicitly not be floored, round them so the logic below can determine which score bar elements to fill
        if (floor === false) {
            calcScoreValue = Math.round(calcScoreValue);
        };

        // set the values to fill the progress bar or not
        if (fill) {
            for (let i = 1; i <= 5; i++) {
                if (calcScoreValue >= i) {
                    scores[i - 1] = `${style.fill} ${classes.fill}`;
                } else {
                    scores[i - 1] = `${style.blank} ${classes.blank}`;
                }
            }
        } else {
            for (let i = 1; i <= 5; i++) {
                if (calcScoreValue >= i && calcScoreValue < (i + 1)) {
                    scores[i - 1] = `${style.fill} ${classes.fill}`;
                } else {
                    scores[i - 1] = `${style.blank} ${classes.blank}`;
                }
            }
        }

        // build the svg item list
        for (let i = 0; i < scoreMax; i++) {
            const active = calcScoreValue === (i + 1);

            elements.push(
                <rect
                    rx={ borderRadius }
                    ry={ borderRadius }
                    x={ i * svgXOffset }
                    y={ active ? svgActiveYOffset : svgYOffset }
                    width={ svgWidth }
                    height={ active ? svgActiveHeight : svgHeight }
                    className={ scores[i] }
                    key={ `svg-rect-${i}` }
                />
            );
        }

        const svgStyle = {};

        if (!autoScaleWidth) {
            svgStyle.width = `${viewBoxWidth}px`;
            svgStyle.height = `${viewBoxHeight}px`;
        }

        return (
            <section className={ `${style.scoreBar} ${classes.scoreBar ? classes.scoreBar : ''}` }>
                { children }
                <div className={ `${style.svgContainer} ${classes.svgContainer ? classes.svgContainer : ''}` }>
                    <svg
                        version="1.0"
                        x="0px"
                        y="0px"
                        viewBox={ `0 0 ${viewBoxWidth} ${viewBoxHeight}` }
                        style={ svgStyle }
                    >
                        <g>
                            { elements }
                        </g>
                    </svg>
                </div>
            </section>
        );
    }
}

Normal.propTypes = {
    scoreMin: PropTypes.number,
    scoreMax: PropTypes.number,
    scoreValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fill: PropTypes.bool,
    floor: PropTypes.bool,
    classes: PropTypes.shape({
        fill: PropTypes.string,
        blank: PropTypes.string,
        scoreBar: PropTypes.string,
        halfScoreFilling: PropTypes.string,
        svgContainer: PropTypes.string
    }),
    autoScaleWidth: PropTypes.bool,
    scoreBarConfig: PropTypes.shape({
        barWidth: PropTypes.number,
        barMargin: PropTypes.number,
        barHeight: PropTypes.number,
        activeBarHeight: PropTypes.number,
        barBorderRadius: PropTypes.number,
        filled: PropTypes.bool
    })
};

Normal.defaultProps = {
    scoreMin: 1,
    scoreMax: 1,
    fill: false,
    floor: true,
    classes: PropTypes.shape({
        fill: '',
        blank: '',
        scoreBar: '',
        halfScoreFilling: '',
        svgContainer: ''
    }),
    autoScaleWidth: true,
    scoreBarConfig: {
        barWidth: 36,
        barMargin: 0,
        barHeight: 8,
        activeBarHeight: 11,
        barBorderRadius: 5
    }
};
